import ApiService from "./apiServices";
import { rest_api_url } from "../constants/urls";
import { fetchReportDownloadUrl } from "../pages/reporting/generated-reports/utils";

export const STARTED_MESSAGE = "Report generation has started. The file will download automatically when it’s ready.";

const POLL_INTERVAL_MS = 5000;
const POLL_TIMEOUT_MS = 10 * 60 * 1000;

const get = (url) =>
  new Promise((resolve, reject) => {
    ApiService.get(url, resolve, reject, true);
  });

const listReports = async () => {
  const response = await get(`${rest_api_url.back_office_generated_reports}?page=1&pageLimit=50`).catch(() => null);
  return Array.isArray(response?.reports) ? response.reports : null;
};

let reportUserId = null;

export const setReportUserId = (id) => {
  reportUserId = id;
};

const claimed = new Set();

export const claimReport = (reportId) => {
  if (reportId == null) return false;
  const key = String(reportId);
  if (claimed.has(key)) return false;
  claimed.add(key);
  return true;
};

const pollForReport = async ({ knownIds, alert_notify }) => {
  const userId = reportUserId;
  if (userId == null) return;

  const deadline = Date.now() + POLL_TIMEOUT_MS;

  while (Date.now() < deadline) {
    await new Promise((resolve) => setTimeout(resolve, POLL_INTERVAL_MS));

    if (!localStorage.getItem("access_token")) return;

    const reports = await listReports();
    if (!reports) continue;

    const mine = reports
      .filter(
        (report) =>
          !knownIds.has(report.id) &&
          !claimed.has(String(report.id)) &&
          String(report.user_id) === String(userId),
      )
      .sort((a, b) => a.id - b.id)[0];
    if (!mine) continue;

    const status = String(mine.status).toLowerCase();
    if (status === "failed") {
      if (claimReport(mine.id)) alert_notify?.("error", `Report for ${mine.name} failed.`);
      continue;
    }
    if (status !== "successful") continue;

    if (claimReport(mine.id)) {
      await fetchReportDownloadUrl({ reportId: mine.id, alert_notify });
    }
    return;
  }
};

export const downloadReport = async ({
  apiUrl,
  pageKey,
  type = "csv",
  sortField,
  sortDirection,
  alert_notify,
  setPendingDownloadLoading,
  extraParams,
  filterProcessor,
  successFallbackMessage = "Report generation started.",
  showSuccessToast = true,
  showStartToast = true,
  startDelay = 0,
}) => {
  setPendingDownloadLoading?.(true);
  try {
    if (showStartToast) alert_notify("info", STARTED_MESSAGE);
    if (startDelay) await new Promise((resolve) => setTimeout(resolve, startDelay));

    const existing = await listReports();

    let url = `${apiUrl}?format=${type}`;

    if (pageKey) {
      const searchFilterData = JSON.parse(localStorage.getItem("searchFilterData"));
      const filterData = searchFilterData?.[pageKey] || {};
      const entries = filterProcessor ? filterProcessor(filterData) : Object.entries(filterData);
      entries.forEach(([key, value]) => {
        if (value !== undefined && value !== null && value !== "") {
          url += `&${encodeURIComponent(key)}=${encodeURIComponent(value)}`;
        }
      });
    }

    if (extraParams) {
      Object.entries(extraParams).forEach(([key, value]) => {
        if (value !== undefined && value !== null && value !== "") {
          url += `&${encodeURIComponent(key)}=${encodeURIComponent(value)}`;
        }
      });
    }

    if (sortField) url += `&sortField=${encodeURIComponent(sortField)}`;
    if (sortDirection) url += `&sortOrder=${encodeURIComponent(sortDirection)}`;

    await new Promise((resolve, reject) => {
      ApiService.get(
        url,
        (response) => {
          if (showSuccessToast) {
            alert_notify("info", response?.message || successFallbackMessage);
          }
          resolve(response);
        },
        (error) => {
          reject(error);
        },
        true,
      );
    });

    if (existing) {
      pollForReport({ knownIds: new Set(existing.map((report) => report.id)), alert_notify }).catch(() => {});
    }
  } catch (error) {
    alert_notify("error", "Something went wrong while generating the report.");
  } finally {
    setPendingDownloadLoading?.(false);
  }
};
